import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText'];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleText.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleText.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    this.fetchAndUpdateCartData(false);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
  }

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    // console.log('cart-icon',event);
    await this.fetchAndUpdateCartData(true);
  };

  /**
   * Fetches cart data and updates the bubble and header price.
   * @param {boolean} animate - Whether to animate the cart bubble transition.
   */
  fetchAndUpdateCartData = async (animate = false) => {
    try {
      const response = await fetch(`/cart.js?t=${Date.now()}`, { headers: { 'Cache-Control': 'no-cache' } });
      const data = await response.json();
      
      const count = data.items ? data.items.length : 0;
      const total = data.total_price || 0;
      
      await this.renderCartBubble(count, animate, total);
    } catch (error) {
      console.error('[CartIcon] Failed to fetch cart data:', error);
    }
  };

  /**
   * Renders the cart bubble and header price.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} animate - Whether to animate.
   * @param {number} cartTotal - The cart total price in cents.
   */
  renderCartBubble = async (itemCount, animate = true, cartTotal = 0) => {
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('cart-bubble--animating', itemCount > 0 && animate);
    this.currentCartCount = itemCount;

    const moneyFormatAttr = document.querySelector('[data-wc_money_formate]')?.getAttribute('data-wc_money_formate');
    const formatMoney = typeof window.replaceAmount === 'function' ? window.replaceAmount : (typeof replaceAmount === 'function' ? replaceAmount : null);
    if (formatMoney && typeof moneyFormatAttr === 'string') {
      document.querySelectorAll('.header_cart-price').forEach((cartTotalEl) => {
        cartTotalEl.innerHTML = formatMoney(moneyFormatAttr, cartTotal);
      });
    }

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate) return;
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}




